import {debounce} from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, ReactNode, useCallback, useState} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../libs/shared/core/src/helpers/internationalization/message';
import {MouseEventHandler, Renderer} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  CalendarTypesEnum,
  ChangeCalendarFocusedDayMethodsEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../libs/shared/core/src/services/tracking/tracking';
import {CalendarsPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {GridItem} from '../../../libs/shared/ui/src/components/grid/grid';
import {stretch} from '../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {DatePickerContainer} from '../common/dateTime/datePickerContainer';
import {ResizeSeparator} from '../common/resizeSeparator';
import {CalendarsEmptyContainer} from './empty/calendarsEmptyContainer';
import {FindCalendarDropdownBoxContainer} from './findCalendar/findCalendarDropdownBoxContainer';
import {LargeCalendarsControlsContainer} from './largeCalendarsControlsContainer';
import {CalendarsListContainer} from './list/calendarsListContainer';

/*
 * Constants.
 */

const minCalendarsLeftPanelWidth = 234;
const maxCalendarsLeftPanelWidth = 300;

/*
 * Props.
 */

export type RenderLargeCalendarsLeftPanel = (width: number, content: ReactNode) => ReactNode;

interface LargeCalendarsBaseProps {
  panelId: CalendarsPanelId;
  renderLeftPanel: RenderLargeCalendarsLeftPanel;
  renderRightPanel: Renderer;
  renderPopover: Renderer | undefined;
  renderGrid: Renderer;
  hasNoCalendars: boolean;
  onGridMouseDown: MouseEventHandler;
  focusedDate: number;
  onFocusedDateSelect: (arg: number) => void;
  leftPanelWidth: number;
  onLeftPanelWidthChange: (arg: number) => void;
  shouldCombineCalendarsList: boolean;
  shouldSaveCalendarVersion: boolean;
}

/*
 * Styles.
 */

const StyledGridDiv = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr auto;

  min-height: 250px;
  height: 100%;
`;

const StyledStretchGridItem = styled(GridItem)`
  ${stretch()}
`;

const StyledDatePickerDiv = styled.div`
  padding: 0px 16px 16px 16px;
`;

const StyledFindCalendarWrapperDiv = styled.div`
  padding: 0px 16px;
`;

const StyledCalendarWrapperDiv = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: hidden;
  overflow-x: scroll;
`;

const StyledCalendarContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  width: 100%;
  height: 100%;
  overflow: hidden;
  min-width: 460px;
`;

const StyledGridWrapperDiv = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;
`;

/*
 * Component.
 */

export const LargeCalendarsBase: FC<LargeCalendarsBaseProps> = (props) => {
  const {focusedDate, onFocusedDateSelect, onLeftPanelWidthChange} = props;
  const theme = useUserTheme();
  const localize = useLocalize();

  /*
   * CalendarList width.
   */

  const [leftPanelWidth, setLeftPanelWidth] = useState(props.leftPanelWidth);
  const calendarsLeftPanelWidth = Math.max(
    minCalendarsLeftPanelWidth,
    Math.min(leftPanelWidth, maxCalendarsLeftPanelWidth),
  );

  const onLeftPanelWidthChangeDebounce = useCallback(debounce(onLeftPanelWidthChange, 5000), [
    onLeftPanelWidthChange,
  ]);

  const onLeftPanelResize: (arg: number) => void = useCallback(
    (newWidth) => {
      setLeftPanelWidth(newWidth);
      onLeftPanelWidthChangeDebounce(newWidth);
    },
    [onLeftPanelWidthChangeDebounce],
  );

  /*
   * Render.
   */

  const {panelId, hasNoCalendars, renderLeftPanel} = props;
  const renderContent = () => {
    if (hasNoCalendars) {
      return <CalendarsEmptyContainer />;
    }

    return (
      <StyledCalendarWrapperDiv>
        <StyledCalendarContentDiv>
          <LargeCalendarsControlsContainer panelId={panelId} />
          <StyledGridWrapperDiv>{props.renderGrid()}</StyledGridWrapperDiv>
        </StyledCalendarContentDiv>
      </StyledCalendarWrapperDiv>
    );
  };

  return (
    <StyledGridDiv onMouseDown={props.onGridMouseDown}>
      <StyledStretchGridItem column={1} style={{width: calendarsLeftPanelWidth}}>
        {renderLeftPanel(
          calendarsLeftPanelWidth,
          <>
            <StyledDatePickerDiv>
              <DatePickerContainer
                selectedDate={DateTime.fromMillis(focusedDate)}
                onDateSelect={(newDateTime) => {
                  onFocusedDateSelect(newDateTime.valueOf());
                  trackInterfaceEvent(InterfaceEventsEnum.CHANGE_CALENDAR_FOCUSED_DAY, {
                    method: ChangeCalendarFocusedDayMethodsEnum.DAY_PICKER,
                    calendar_type: CalendarTypesEnum.LARGE,
                  });
                }}
                primaryColor={theme.greys.shade80}
                secondaryColor={theme.greys.shade70}
                disabledColor={theme.greys.shade50}
                backgroundHoverColor={theme.alphas.gray10}
              />
            </StyledDatePickerDiv>
            <StyledFindCalendarWrapperDiv>
              <FindCalendarDropdownBoxContainer
                panelId={props.panelId}
                inputBackgroundColor={theme.greys.shade30}
                placeholder={
                  props.shouldCombineCalendarsList
                    ? localize(
                        defineMessage({
                          id: 'calendar-add-calendar',
                          message: 'Add a calendar',
                        }),
                      )
                    : undefined
                }
              />
            </StyledFindCalendarWrapperDiv>
            <CalendarsListContainer
              panelId={props.panelId}
              shouldCombineCalendarsList={props.shouldCombineCalendarsList}
              shouldSaveCalendarVersion={props.shouldSaveCalendarVersion}
            />
          </>,
        )}
      </StyledStretchGridItem>
      <StyledStretchGridItem column={3}>{renderContent()}</StyledStretchGridItem>
      <StyledStretchGridItem column={4}>{props.renderRightPanel()}</StyledStretchGridItem>
      <ResizeSeparator
        column={2}
        size={calendarsLeftPanelWidth}
        minSize={minCalendarsLeftPanelWidth}
        maxSize={maxCalendarsLeftPanelWidth}
        onSizeChange={onLeftPanelResize}
        onIsResizingChange={() => onLeftPanelWidthChangeDebounce.flush()}
        color={theme.greys.shade30}
      />
      {props.renderPopover && props.renderPopover()}
    </StyledGridDiv>
  );
};
